import type { LabResult } from './localData';

function normalized(value?: string): string {
  return (value || '').normalize('NFKC').trim().replace(/\s+/g, ' ').toLocaleLowerCase('ru-RU');
}

function sameIndicator(left: LabResult, right: LabResult): boolean {
  const leftCode = normalized(left.canonicalCode);
  const rightCode = normalized(right.canonicalCode);
  const leftName = normalized(left.originalName || left.analyte);
  const rightName = normalized(right.originalName || right.analyte);

  return (Boolean(leftCode) && leftCode === rightCode) || (Boolean(leftName) && leftName === rightName);
}

function sameValue(left: LabResult, right: LabResult): boolean {
  if (left.value !== undefined || right.value !== undefined) return left.value === right.value;
  return normalized(left.valueText) === normalized(right.valueText);
}

function sameManualEntry(left: LabResult, right: LabResult): boolean {
  return left.owner === right.owner
    && left.country === right.country
    && left.takenOn === right.takenOn
    && normalized(left.labName) === normalized(right.labName)
    && normalized(left.unit) === normalized(right.unit)
    && sameIndicator(left, right)
    && sameValue(left, right);
}

function hasSourceLocator(result: LabResult): result is LabResult & { documentId: string; sourcePage: number; sourceRow: number } {
  return Boolean(result.documentId)
    && Number.isInteger(result.sourcePage) && result.sourcePage! > 0
    && Number.isInteger(result.sourceRow) && result.sourceRow! > 0;
}

// A source row, not a medical name/value, is the only automatic duplicate.
// Identical names and values are normal in reports (including within one
// document), and manual entries remain confirmable even when they look alike.
export function isDuplicateLabResult(candidate: LabResult, existing: LabResult): boolean {
  return hasSourceLocator(candidate)
    && hasSourceLocator(existing)
    && candidate.owner === existing.owner
    && candidate.documentId === existing.documentId
    && candidate.sourcePage === existing.sourcePage
    && candidate.sourceRow === existing.sourceRow;
}

// Includes duplicates already stored and duplicates within the current form.
// The caller can still show every AI suggestion, but only unique rows proceed
// to the confirmation save.
export function duplicateLabResultIndexes(candidates: LabResult[], existing: LabResult[]): Set<number> {
  const seen = [...existing];
  const duplicates = new Set<number>();
  candidates.forEach((candidate, index) => {
    if (seen.some(item => isDuplicateLabResult(candidate, item))) duplicates.add(index);
    else seen.push(candidate);
  });
  return duplicates;
}

// A non-blocking safety cue for manual entries. It deliberately has no effect
// on saving because a matching value can be a legitimate second measurement.
export function similarManualLabResultIndexes(candidates: LabResult[], existing: LabResult[]): Set<number> {
  const similar = new Set<number>();
  candidates.forEach((candidate, index) => {
    if (hasSourceLocator(candidate)) return;
    const others = [...existing, ...candidates.slice(0, index)];
    if (others.some(other => !hasSourceLocator(other) && sameManualEntry(candidate, other))) similar.add(index);
  });
  return similar;
}
